
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowUp, 
  ArrowDown, 
  Star, 
  Puzzle, 
  ThumbsUp, 
  HelpCircle,
  Calculator,
  Search
} from "lucide-react";

// Sample menu items data
const menuItems = [
  { 
    id: 1, 
    name: 'Ropa Vieja Canaria', 
    category: 'Mains', 
    price: 32.99, 
    cost: 12.50, 
    popularity: 85, 
    quadrant: 'star',
    recommendedAction: 'promote',
    seasonalIngredients: ['carne de res', 'pimientos']
  },
  { 
    id: 2, 
    name: 'Caldo de Pescado', 
    category: 'Starters', 
    price: 15.99, 
    cost: 8.75, 
    popularity: 72, 
    quadrant: 'star',
    recommendedAction: 'maintain',
    seasonalIngredients: ['vieja', 'nata']
  },
  { 
    id: 3, 
    name: 'Risotto de Gofio', 
    category: 'Mains', 
    price: 28.50, 
    cost: 14.25, 
    popularity: 65, 
    quadrant: 'puzzle',
    recommendedAction: 'adjust-price',
    seasonalIngredients: ['gofio', 'queso de flor']
  },
  { 
    id: 4, 
    name: 'Ensalada Canaria', 
    category: 'Starters', 
    price: 12.99, 
    cost: 3.50, 
    popularity: 90, 
    quadrant: 'plowhorse',
    recommendedAction: 'redesign',
    seasonalIngredients: ['lechuga', 'queso palmero']
  },
  { 
    id: 5, 
    name: 'Conejo al Salmorejo', 
    category: 'Mains', 
    price: 29.99, 
    cost: 11.25, 
    popularity: 45, 
    quadrant: 'puzzle',
    recommendedAction: 'promote',
    seasonalIngredients: ['conejo', 'tomillo']
  },
  { 
    id: 6, 
    name: 'Bienmesabe', 
    category: 'Desserts', 
    price: 12.50, 
    cost: 3.75, 
    popularity: 78, 
    quadrant: 'star',
    recommendedAction: 'maintain',
    seasonalIngredients: ['almendras', 'huevos']
  },
  { 
    id: 7, 
    name: 'Lapas a la Plancha', 
    category: 'Starters', 
    price: 18.99, 
    cost: 9.50, 
    popularity: 35, 
    quadrant: 'dog',
    recommendedAction: 'remove',
    seasonalIngredients: ['lapas', 'mojo verde']
  },
  { 
    id: 8, 
    name: 'Papas Arrugadas con Mojo', 
    category: 'Sides', 
    price: 9.99, 
    cost: 2.75, 
    popularity: 55, 
    quadrant: 'plowhorse',
    recommendedAction: 'adjust-price',
    seasonalIngredients: ['papas', 'pimentón', 'ajo']
  }
];

// Define seasonal ingredients for limited-time offerings
const seasonalIngredients = [
  { name: 'Tunos', season: 'Spring', priceTrend: 'decreasing' },
  { name: 'Plátanos de Canarias', season: 'Summer', priceTrend: 'stable' },
  { name: 'Calabaza', season: 'Fall', priceTrend: 'increasing' },
  { name: 'Batatas', season: 'Winter', priceTrend: 'stable' },
  { name: 'Mango de Mogán', season: 'Spring', priceTrend: 'decreasing' }
];

// P&L simulation data
const plImpact = {
  currentRevenue: 28500,
  projectedRevenue: 30250,
  currentCost: 11400,
  projectedCost: 11850,
  currentProfit: 17100,
  projectedProfit: 18400
};

const QuadrantBadge = ({ type }: { type: string }) => {
  switch (type) {
    case 'star':
      return <Badge className="bg-amber-500 hover:bg-amber-600"><Star className="mr-1 h-3 w-3" /> Star</Badge>;
    case 'puzzle':
      return <Badge className="bg-indigo-500 hover:bg-indigo-600"><Puzzle className="mr-1 h-3 w-3" /> Puzzle</Badge>;
    case 'plowhorse':
      return <Badge className="bg-green-500 hover:bg-green-600"><ThumbsUp className="mr-1 h-3 w-3" /> Plowhorse</Badge>;
    case 'dog':
      return <Badge className="bg-red-500 hover:bg-red-600"><HelpCircle className="mr-1 h-3 w-3" /> Dog</Badge>;
    default:
      return <Badge>Unknown</Badge>;
  }
};

const ActionBadge = ({ action }: { action: string }) => {
  switch (action) {
    case 'promote':
      return <Badge className="bg-green-500 hover:bg-green-600"><ArrowUp className="mr-1 h-3 w-3" /> Promote</Badge>;
    case 'remove':
      return <Badge className="bg-red-500 hover:bg-red-600"><ArrowDown className="mr-1 h-3 w-3" /> Remove</Badge>;
    case 'adjust-price':
      return <Badge className="bg-blue-500 hover:bg-blue-600"><Calculator className="mr-1 h-3 w-3" /> Adjust Price</Badge>;
    case 'redesign':
      return <Badge className="bg-purple-500 hover:bg-purple-600">Redesign</Badge>;
    case 'maintain':
      return <Badge className="bg-gray-500 hover:bg-gray-600">Maintain</Badge>;
    default:
      return <Badge>Unknown</Badge>;
  }
};

const MenuEngineering = () => {
  const [activeTab, setActiveTab] = useState("analysis");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  // Get unique categories
  const categories = ["All", ...new Set(menuItems.map(item => item.category))];

  // Filter menu items based on search and category
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate contribution margin and contribution margin percentage
  const calculateMargin = (price: number, cost: number) => {
    const margin = price - cost;
    const percentage = (margin / price) * 100;
    return {
      margin: margin.toFixed(2),
      percentage: percentage.toFixed(1)
    };
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">Menu Performance</h1>
      </div>

      <Tabs defaultValue="analysis" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="analysis" onClick={() => setActiveTab("analysis")}>
            Menu Analysis
          </TabsTrigger>
          <TabsTrigger value="quadrants" onClick={() => setActiveTab("quadrants")}>
            Quadrant View
          </TabsTrigger>
          <TabsTrigger value="recommendations" onClick={() => setActiveTab("recommendations")}>
            Recommendations
          </TabsTrigger>
          <TabsTrigger value="simulator" onClick={() => setActiveTab("simulator")}>
            P&L Simulator
          </TabsTrigger>
        </TabsList>

        {/* Menu Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Menu Item Performance</CardTitle>
              <CardDescription>
                Analyze the contribution margin and popularity of your menu items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Label htmlFor="search">Search Items</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
                    <Input 
                      id="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8"
                      placeholder="Search by name..."
                    />
                  </div>
                </div>
                <div className="w-full md:w-48">
                  <Label htmlFor="category">Category</Label>
                  <select 
                    id="category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-md border">
                <div className="grid grid-cols-7 bg-muted p-3 rounded-t-md font-medium">
                  <div className="col-span-2">Item</div>
                  <div>Price</div>
                  <div>Cost</div>
                  <div>Margin</div>
                  <div>Popularity</div>
                  <div>Quadrant</div>
                </div>
                <div className="divide-y">
                  {filteredItems.map(item => {
                    const margin = calculateMargin(item.price, item.cost);
                    return (
                      <div key={item.id} className="grid grid-cols-7 p-3 items-center hover:bg-muted/50">
                        <div className="col-span-2 font-medium">{item.name}</div>
                        <div>${item.price.toFixed(2)}</div>
                        <div>${item.cost.toFixed(2)}</div>
                        <div>${margin.margin} ({margin.percentage}%)</div>
                        <div>{item.popularity}%</div>
                        <div><QuadrantBadge type={item.quadrant} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quadrant View Tab */}
        <TabsContent value="quadrants" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Menu Item Quadrants</CardTitle>
                <CardDescription>
                  Items categorized by profitability and popularity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="border rounded-md p-4 bg-amber-50 dark:bg-amber-900/20">
                    <h3 className="font-semibold text-lg mb-2 flex items-center justify-center">
                      <Star className="mr-2 h-5 w-5 text-amber-500" /> Stars
                    </h3>
                    <p className="text-sm mb-4">High profit, high popularity</p>
                    <ul className="space-y-2">
                      {menuItems.filter(item => item.quadrant === 'star').map(item => (
                        <li key={item.id} className="p-2 bg-background rounded shadow-sm">
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border rounded-md p-4 bg-indigo-50 dark:bg-indigo-900/20">
                    <h3 className="font-semibold text-lg mb-2 flex items-center justify-center">
                      <Puzzle className="mr-2 h-5 w-5 text-indigo-500" /> Puzzles
                    </h3>
                    <p className="text-sm mb-4">High profit, low popularity</p>
                    <ul className="space-y-2">
                      {menuItems.filter(item => item.quadrant === 'puzzle').map(item => (
                        <li key={item.id} className="p-2 bg-background rounded shadow-sm">
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border rounded-md p-4 bg-green-50 dark:bg-green-900/20">
                    <h3 className="font-semibold text-lg mb-2 flex items-center justify-center">
                      <ThumbsUp className="mr-2 h-5 w-5 text-green-500" /> Plowhorses
                    </h3>
                    <p className="text-sm mb-4">Low profit, high popularity</p>
                    <ul className="space-y-2">
                      {menuItems.filter(item => item.quadrant === 'plowhorse').map(item => (
                        <li key={item.id} className="p-2 bg-background rounded shadow-sm">
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border rounded-md p-4 bg-red-50 dark:bg-red-900/20">
                    <h3 className="font-semibold text-lg mb-2 flex items-center justify-center">
                      <HelpCircle className="mr-2 h-5 w-5 text-red-500" /> Dogs
                    </h3>
                    <p className="text-sm mb-4">Low profit, low popularity</p>
                    <ul className="space-y-2">
                      {menuItems.filter(item => item.quadrant === 'dog').map(item => (
                        <li key={item.id} className="p-2 bg-background rounded shadow-sm">
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Menu Item Recommendations</CardTitle>
              <CardDescription>
                Actionable insights to optimize your menu
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="grid grid-cols-6 bg-muted p-3 rounded-t-md font-medium">
                  <div className="col-span-2">Item</div>
                  <div>Quadrant</div>
                  <div>Current Price</div>
                  <div>Recommended Action</div>
                  <div>Details</div>
                </div>
                <div className="divide-y">
                  {menuItems.map(item => (
                    <div key={item.id} className="grid grid-cols-6 p-3 items-center hover:bg-muted/50">
                      <div className="col-span-2 font-medium">{item.name}</div>
                      <div><QuadrantBadge type={item.quadrant} /></div>
                      <div>${item.price.toFixed(2)}</div>
                      <div><ActionBadge action={item.recommendedAction} /></div>
                      <div>
                        <Button variant="outline" size="sm">View</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Seasonal Ingredients</CardTitle>
              <CardDescription>
                Limited-time offering opportunities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <div className="grid grid-cols-4 bg-muted p-3 rounded-t-md font-medium">
                  <div className="col-span-2">Ingredient</div>
                  <div>Season</div>
                  <div>Price Trend</div>
                </div>
                <div className="divide-y">
                  {seasonalIngredients.map((ingredient, index) => (
                    <div key={index} className="grid grid-cols-4 p-3 items-center hover:bg-muted/50">
                      <div className="col-span-2 font-medium">{ingredient.name}</div>
                      <div>{ingredient.season}</div>
                      <div className={
                        ingredient.priceTrend === 'decreasing' 
                          ? 'text-green-500' 
                          : ingredient.priceTrend === 'increasing' 
                            ? 'text-red-500' 
                            : 'text-amber-500'
                      }>
                        {ingredient.priceTrend === 'decreasing' && <ArrowDown className="inline h-4 w-4 mr-1" />}
                        {ingredient.priceTrend === 'increasing' && <ArrowUp className="inline h-4 w-4 mr-1" />}
                        {ingredient.priceTrend.charAt(0).toUpperCase() + ingredient.priceTrend.slice(1)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* P&L Simulator Tab */}
        <TabsContent value="simulator" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>P&L Impact Simulator</CardTitle>
              <CardDescription>
                Simulate the financial impact of menu changes before implementation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Current</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenue</p>
                        <p className="text-2xl font-bold">${plImpact.currentRevenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Cost</p>
                        <p className="text-2xl font-bold">${plImpact.currentCost.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Profit</p>
                        <p className="text-2xl font-bold">${plImpact.currentProfit.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Projected</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenue</p>
                        <p className="text-2xl font-bold">${plImpact.projectedRevenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Cost</p>
                        <p className="text-2xl font-bold">${plImpact.projectedCost.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Profit</p>
                        <p className="text-2xl font-bold">${plImpact.projectedProfit.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Difference</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenue</p>
                        <p className="text-2xl font-bold text-green-500">
                          +${(plImpact.projectedRevenue - plImpact.currentRevenue).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Cost</p>
                        <p className="text-2xl font-bold text-amber-500">
                          +${(plImpact.projectedCost - plImpact.currentCost).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Profit</p>
                        <p className="text-2xl font-bold text-green-500">
                          +${(plImpact.projectedProfit - plImpact.currentProfit).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Simulate Menu Changes</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="item">Select Menu Item</Label>
                      <select 
                        id="item"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {menuItems.map(item => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="price">New Price</Label>
                      <Input id="price" type="number" min="0" step="0.01" placeholder="0.00" />
                    </div>
                    <div>
                      <Label htmlFor="volume">Expected Volume Change</Label>
                      <div className="flex items-center">
                        <Input id="volume" type="number" min="-100" max="100" placeholder="0" />
                        <span className="ml-2">%</span>
                      </div>
                    </div>
                  </div>
                  <Button>Calculate Impact</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MenuEngineering;
